/**
 * CORE-C GAP-004 / SEC-001 — the Passport section audience contract.
 *
 * ============================================================================
 * WHAT THIS IS, AND WHAT IT IS NOT
 * ============================================================================
 * This answers exactly one question:
 *
 *   Given data the application is permitted to possess, may this Passport
 *   section RENDER to this viewer?
 *
 * It does NOT answer "may this viewer retrieve this data at all". That is a
 * server question, answered by `can_view_passport_data(owner, viewer)` and by
 * the RLS on each producing table, and it stays there. Nothing in this file
 * widens, narrows or re-states a database policy, and no test built on it may
 * be read as proof of server-side non-disclosure.
 *
 * The constitution is explicit that the client layer is not load-bearing:
 * "Two layers: server-side nullification in get_in_scope_passport /
 * get_talent_profile / get_visible_talent, and filterPassportFields
 * client-side as explicitly documented defence in depth. That ordering is
 * correct — the client layer is not load-bearing." This contract is a third
 * thing again — a COMPOSITION rule — and it is subject to the same ordering.
 *
 * ============================================================================
 * WHY A CONTRACT RATHER THAN A PROP
 * ============================================================================
 * CORE-C-SEC-001, recorded: "A section added without view-mode awareness would
 * leak by default; no enforced contract requires it." The self-view checklist
 * carries the same line as an unmet obligation: "new sections cannot be added
 * without declaring a view mode ✗".
 *
 * Measured on 2026-09-11, that was exactly the state. Of the 22 sections the
 * cross-person Passport composes, ONE had a real audience rule in code
 * (`PassportDiagnosticCompetency`) and one more self-gated internally
 * (`ToolboxSection`). The rest were governed by:
 *
 *   · `RequireFeature`   — a TIER gate. It asks whether the shop's plan
 *                          includes a feature, never whether this viewer may
 *                          see this person's data. Nine sections had one and
 *                          nothing else.
 *   · `isOwner` /        — EDIT affordances. They hide buttons and switches;
 *     `isReadOnly`         the content renders either way.
 *   · `isSelfView`       — an optional prop, wired by hand at each call site,
 *                          and therefore forgotten at some of them.
 *
 * The forgetting is not hypothetical. `PassportHome` passed `isSelfView` to
 * Diagnostic Competency and `TechnicianPassport` did not, so the ruling of
 * 2026-09-07 — "the subject can see what the record says about them" — held on
 * one route and silently not on the other.
 *
 * So the declaration is centralised and mandatory, and an undeclared section
 * renders to nobody.
 *
 * ============================================================================
 * THE VOCABULARY IS THE CONSTITUTION'S OWN
 * ============================================================================
 * "CORE-Q determines audience / view mode (self | in_scope | connect)" — and
 * `PassportViewMode` in `./fieldVisibility` already carries those three. They
 * are reused rather than replaced.
 *
 * A NOTE ON WHAT IS NOT SPELLED OUT BELOW. CORE-N's read boundary scans every
 * source file that NAMES its section table and requires each to be classified
 * as a reader of it. This file reads nothing — it names a policy, not a table —
 * and none of that suite's five kinds describes "documentation". So the `why`
 * strings name the POLICY and let the section id carry the table. The exact
 * table list is in the CORE-C readiness record. Please do not paste the
 * identifiers back in.
 *
 * ONE refinement is added, because the product already expresses it and has
 * since before this contract existed: `in_scope_manager`. Diagnostic
 * Competency renders for MANAGER/OWNER and not for a SERVICE_WRITER, while
 * `can_view_passport_data` grants its same-shop employer right to
 * OWNER/MANAGER/**WRITER** alike. That is a real distinction inside `in_scope`,
 * and it is named rather than smuggled in as a boolean.
 *
 * `in_scope_manager` IMPLIES `in_scope`: a manager holds both, so a section
 * declaring `in_scope` is visible to managers without having to say so twice.
 */

import type { PassportViewMode } from './fieldVisibility';

/**
 * The audience a viewer holds relative to one Passport.
 *
 * A superset of `PassportViewMode` by exactly one member; the three shared
 * names mean what they mean there.
 */
export type PassportAudience = PassportViewMode | 'in_scope_manager';

/** Every audience this contract recognises. Anything else is not an audience. */
export const PASSPORT_AUDIENCES: readonly PassportAudience[] = [
  'self',
  'in_scope',
  'in_scope_manager',
  'connect',
] as const;

/**
 * How a section's audience list was arrived at.
 *
 *  - `constitutional` — a SHALL or a recorded product ruling decides it.
 *  - `server_policy`  — `can_view_passport_data` already grants exactly this,
 *                       and the composition is being brought into line with it.
 *  - `current_behaviour` — no higher authority speaks; the section renders to
 *                       these audiences today and narrowing it would be a
 *                       product decision CORE-C has not been given.
 *  - `unresolved`     — the intended audience cannot be determined from
 *                       existing evidence. See `UNRESOLVED` below.
 */
export type AudienceBasis =
  | 'constitutional'
  | 'server_policy'
  | 'current_behaviour'
  | 'unresolved';

export interface PassportSectionAudienceRule {
  /** The audiences permitted to see this section. Empty means nobody. */
  readonly audiences: readonly PassportAudience[];
  readonly basis: AudienceBasis;
  /** Why this list, in one line. Required — a rule without a reason is a guess. */
  readonly why: string;
}

/**
 * THE SELF-VIEW FLOOR.
 *
 * CORE-C SHALL "present, to the person, every substantive fact the Passport
 * exposes to any other human audience." A section shown to an employer and not
 * to its subject is, in the legacy law's words, not a professional record but
 * a file.
 *
 * So `self` is not a judgement call on any section that renders to anyone
 * else — it is derived. `assertSelfViewFloor` below fails the build's test if
 * a declaration ever violates it, which is what makes it a floor rather than a
 * habit.
 */
function withSelf(
  audiences: readonly Exclude<PassportAudience, 'self'>[],
): readonly PassportAudience[] {
  return ['self', ...audiences];
}

/**
 * The declarations, for every CROSS-PERSON Passport composition.
 *
 * `PassportHome` is deliberately absent: it resolves its subject from
 * `useAuth().user.id` and has no other person to leak, so there is nothing for
 * an audience contract to decide there. Adding one for symmetry would be
 * ceremony.
 *
 * TWO surfaces compose these declarations, and the table is shared because a
 * section is one section wherever it is drawn:
 *
 *   · `TechnicianPassport` (`/technicians/:id`) — the cross-person Passport.
 *     Its viewers hold `self`, `in_scope` and/or `in_scope_manager`; it never
 *     produces `connect`.
 *   · `TalentProfileView` (`/connect/talent/:userId`,
 *     `/enterprise/talent/:userId`) — the Connect and enterprise projection.
 *     Its viewers hold exactly one of `self`, `connect`, `in_scope`.
 *
 * `ManagerPassport` and `AdvisorPassport` are absent, and that is a MEASURED
 * conclusion rather than an omission: both gate the WHOLE PAGE before any
 * section mounts, so every section on them shares one audience and a
 * per-section declaration would decide nothing. Audited 2026-09-13.
 */
export const PASSPORT_SECTION_AUDIENCE: Readonly<
  Record<string, PassportSectionAudienceRule>
> = {
  'snapshot-card': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'Identity and summary of the record; renders to every permitted viewer today.',
  },
  'pensieve-summary': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'OptiTime learning coverage; renders to every permitted viewer today.',
  },
  'employment-history': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'CORE-D employment record; renders to every permitted viewer today.',
  },
  'role-tracks': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'Catalyst role history; renders to every permitted viewer today.',
  },
  'proficiencies': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'CORE-E category proficiency; renders to every permitted viewer today.',
  },
  'skill-heatmap': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'CORE-F skill projection; renders to every permitted viewer today.',
  },
  'performance-dna': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'CORE-G performance figures; renders to every permitted viewer today.',
  },
  'time-signature': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'Minute productivity shape; renders to every permitted viewer today.',
  },
  'inspection-metrics': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'Workflow inspection book-vs-actual; renders to every permitted viewer today.',
  },
  'pensieve-insights': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'OptiTime learning detail; renders to every permitted viewer today.',
  },
  'pensieve-coaching': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'OptiTime coaching detail; renders to every permitted viewer today.',
  },
  'diagnostic-competency': {
    audiences: withSelf(['in_scope']),
    basis: 'constitutional',
    why:
      'Founder addendum 2026-10-20 P1/P2 (supersedes the in_scope_manager-only reading of the 2026-09-07 ' +
      'ruling): Service Advisors of the shop and ORG_ADMINs of its organisation view professional competency ' +
      'analytics read-only. Server: can_view_technician_passport on technician_diagnostic_stats.',
  },

  'ticket-ledger': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'CORE-J ticket history; renders to every permitted viewer today.',
  },
  'toolbox': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why:
      'Mounts for every permitted viewer BY DESIGN — it shows a locked "Toolbox is private to ' +
      'the technician" placeholder to a non-owner and gates the inventory itself on ' +
      '`useIsOwnProfile`. The contract governs mounting; the private content stays gated inside ' +
      'the component. Declaring it self-only here would delete that placeholder, which is a UX ' +
      'change no evidence asks for.',
  },
  'certifications': {
    audiences: withSelf(['connect', 'in_scope']),
    basis: 'server_policy',
    why:
      'SELECT is `visibility = visible_to_shops AND can_view_passport_data(user_id, ' +
      'auth.uid())`. That predicate grants BOTH branch 2 — the Connect path: the subject opted ' +
      'in and is open to work, the viewer is in an active shop, no mutual block — AND branch 3, ' +
      'the same-shop employer right for OWNER/MANAGER/WRITER. All three modes are therefore ' +
      'what the server already permits.',
  },
  'experience': {
    audiences: withSelf(['connect', 'in_scope']),
    basis: 'server_policy',
    why: 'SELECT is can_view_passport_data(...), branches 2 and 3 as above.',
  },
  'education': {
    audiences: withSelf(['connect', 'in_scope']),
    basis: 'server_policy',
    why: 'SELECT is can_view_passport_data(...), branches 2 and 3 as above.',
  },
  'references': {
    audiences: withSelf(['connect', 'in_scope']),
    basis: 'server_policy',
    why:
      'SELECT is can_view_passport_data(...), branches 2 and 3 as above, and the BKR4 chokepoint ' +
      'routes reads through a security-definer RPC that records a sensitive-access entry.',
  },
  'eligibility': {
    audiences: withSelf(['connect', 'in_scope']),
    basis: 'server_policy',
    why: 'SELECT is can_view_passport_data(...), branches 2 and 3 as above.',
  },
  'future-insights': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'Derived from proficiencies and metrics already shown; renders to every permitted viewer today.',
  },
  'learning-center': {
    audiences: withSelf(['in_scope']),
    basis: 'current_behaviour',
    why: 'Training recommendations; renders to every permitted viewer today.',
  },
  'student-verification-summary': {
    audiences: withSelf(['connect', 'in_scope']),
    basis: 'server_policy',
    why:
      'Read through the security-definer RPC get_student_verification_summary(subject, shop), ' +
      'which is the authority and remains so. This declaration is COMPOSITION POLICY ONLY: it ' +
      'says the section may appear in these audiences; it neither performs nor replaces that ' +
      "RPC's authorization. Rendered only when the subject holds the student role.",
  },
  'connect-work-ledger': {
    audiences: withSelf(['connect']),
    basis: 'constitutional',
    why:
      'Core-100 R5. CORE-J Outputs: "Projected evidence, person-controlled -> CORE-R Connect" (l.295: "a design ' +
      'constraint, not an open decision"). Composition only: the server read connect_work_ledger decides — the ' +
      "person's own opt-in (default OFF) AND the strict Connect audience (OD-15). The subject sees a preview of " +
      'exactly what is projected and the switch.',
  },
  'coaching-notes': {
    audiences: withSelf(['in_scope_manager']),
    basis: 'server_policy',
    why:
      'Manager-authored coaching (technician_profiles.notes, CORE-H) is HR-private: open_technician_passport ' +
      'withholds it from the read-only Passport audience (Service Advisor, ORG_ADMIN) added by the Oct 20 ' +
      'addendum, so the section renders to the subject and to Owners/Managers only.',
  },

};

/** Sections whose intended audience could not be determined. See the report. */
export const UNRESOLVED_SECTIONS: readonly string[] = [];

/**
 * The audiences a viewer holds relative to one Passport.
 *
 * `inScopeEmployer` is a PRESENTATION classification — "this viewer holds an
 * employer role in the active shop" — and deliberately not a claim that the
 * subject is a member of that shop. Whether these two people share a shop is
 * decided by `can_view_passport_data` on the server, which this function
 * cannot and must not second-guess.
 *
 * Returns an EMPTY set when the inputs are not yet known, so a section renders
 * to nobody while the answer is still loading rather than flickering into view.
 */
export function resolveViewerAudiences(ctx: {
  isSelf: boolean;
  /** Viewer holds OWNER/MANAGER/SERVICE_WRITER in the active shop. */
  inScopeEmployer: boolean;
  /** Viewer holds MANAGER/OWNER specifically. */
  isManager: boolean;
  /** True while ownership or role is still resolving. */
  isResolving?: boolean;
}): ReadonlySet<PassportAudience> {
  if (ctx.isResolving) return new Set();

  const held = new Set<PassportAudience>();
  if (ctx.isSelf) held.add('self');
  if (ctx.inScopeEmployer) {
    held.add('in_scope');
    // in_scope_manager implies in_scope, never the reverse.
    if (ctx.isManager) held.add('in_scope_manager');
  }
  return held;
}

/**
 * The audiences a viewer holds on a surface that knows its VIEW MODE rather
 * than the viewer's role — `TalentProfileView`, which derives
 * `self | connect | in_scope` from the subject's identity and the `?mode=`
 * parameter and then asks the matching server RPC.
 *
 * A second derivation into the SAME vocabulary, not a second vocabulary. The
 * two composing surfaces genuinely differ in what they know: one holds a role
 * in a shop, the other holds a mode that has already selected a server
 * authority. Collapsing them would mean inventing a role the Connect page does
 * not have.
 *
 * `in_scope` here does NOT confer `in_scope_manager`. This page never
 * establishes that the viewer is a manager, and the refinement is strict.
 *
 * An unrecognised or absent mode yields the EMPTY set — fail closed. The page's
 * own mode derivation separately falls back to `connect` for an unrecognised
 * `?mode=`; that is a pre-existing contract and this function is the last line,
 * not the first.
 */
export function resolveAudiencesFromViewMode(
  mode: PassportViewMode | null | undefined,
): ReadonlySet<PassportAudience> {
  switch (mode) {
    case 'self':
      return new Set<PassportAudience>(['self']);
    case 'connect':
      return new Set<PassportAudience>(['connect']);
    case 'in_scope':
      return new Set<PassportAudience>(['in_scope']);
    default:
      return new Set<PassportAudience>();
  }
}

/**
 * May this section render to a viewer holding these audiences?
 *
 * FAIL CLOSED in all three ways it can go wrong: an unknown section id, a
 * declaration with no audiences, and a viewer holding none.
 */
export function isSectionVisibleToAudience(
  sectionId: string,
  held: ReadonlySet<PassportAudience>,
): boolean {
  const rule = PASSPORT_SECTION_AUDIENCE[sectionId];
  // An undeclared section renders to nobody. This is the whole point: adding a
  // component to the composition is not enough to show it to another person.
  if (!rule) return false;
  if (rule.audiences.length === 0) return false;
  for (const a of rule.audiences) {
    if (held.has(a)) return true;
  }
  return false;
}

/** Every declared section id, for tests and inspection. */
export function declaredSectionIds(): readonly string[] {
  return Object.keys(PASSPORT_SECTION_AUDIENCE);
}

/**
 * The self-view floor, as a checkable assertion rather than a convention.
 *
 * Returns the ids of any declaration that lets some other audience see a
 * section the subject cannot. Should always be empty; a test fails on it.
 */
export function sectionsViolatingSelfViewFloor(): readonly string[] {
  return declaredSectionIds().filter((id) => {
    const { audiences } = PASSPORT_SECTION_AUDIENCE[id];
    const shownToOthers = audiences.some((a) => a !== 'self');
    return shownToOthers && !audiences.includes('self');
  });
}
